import React from "react";
import PropTypes from "prop-types";
import { Box, Paper, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import Iconify from "../../iconify/Iconify";

const EVENT_TYPE_OPTIONS = [
  {
    value: "legacy",
    title: "Standard-Event",
    description:
      "Für interne Veranstaltungen mit klassischer Teilnehmerliste und manueller Verwaltung.",
    icon: "solar:calendar-mark-bold-duotone",
    color: "#0f766e",
  },
  {
    value: "booking",
    title: "Buchungs-Event",
    description:
      "Für Anmeldungen mit Formular, Sessions und Preisregeln inklusive öffentlichem Buchungslink.",
    icon: "solar:ticket-sale-bold-duotone",
    color: "#1d4ed8",
  },
];

export default function StepEventType({
  value,
  onChange,
  disabledTypeSwitch = false,
  lockReason = "",
}) {
  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h6">Event-Typ wählen</Typography>
        <Typography variant="body2" color="text.secondary">
          Der gewählte Typ definiert den weiteren Ablauf und die verfügbaren Schritte.
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
          gap: 2,
        }}
      >
        {EVENT_TYPE_OPTIONS.map((option) => {
          const isActive = option.value === value;
          const isSelectable = !disabledTypeSwitch || isActive;

          return (
            <Paper
              key={option.value}
              variant="outlined"
              role="button"
              tabIndex={isSelectable ? 0 : -1}
              aria-pressed={isActive}
              onClick={() => {
                if (!isSelectable) return;
                onChange(option.value);
              }}
              onKeyDown={(event) => {
                if (!isSelectable) return;
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onChange(option.value);
                }
              }}
              sx={{
                p: 2,
                borderWidth: isActive ? 2 : 1,
                borderColor: isActive ? option.color : "divider",
                bgcolor: isActive
                  ? alpha(option.color, 0.1)
                  : "background.paper",
                cursor: isSelectable ? "pointer" : "not-allowed",
                opacity: isSelectable ? 1 : 0.58,
                transition: "all 160ms ease",
                "&:hover": isSelectable
                  ? {
                      borderColor: option.color,
                      bgcolor: alpha(option.color, 0.08),
                    }
                  : undefined,
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1.5,
                    border: `1px solid ${alpha(option.color, isActive ? 0.5 : 0.3)}`,
                    bgcolor: alpha(option.color, isActive ? 0.18 : 0.08),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Iconify icon={option.icon} width={22} sx={{ color: option.color }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {option.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {option.description}
                  </Typography>
                </Box>
                {isActive ? (
                  <Iconify
                    icon="solar:check-circle-bold"
                    width={20}
                    sx={{ color: option.color, mt: 0.25 }}
                  />
                ) : null}
              </Stack>
            </Paper>
          );
        })}
      </Box>

      {disabledTypeSwitch && lockReason ? (
        <Typography variant="caption" color="warning.main">
          {lockReason}
        </Typography>
      ) : null}
    </Stack>
  );
}

StepEventType.propTypes = {
  value: PropTypes.oneOf(["legacy", "booking"]).isRequired,
  onChange: PropTypes.func.isRequired,
  disabledTypeSwitch: PropTypes.bool,
  lockReason: PropTypes.string,
};
